import React from 'react';
import { useSelector } from 'react-redux';
import { BarLoader } from 'react-spinners';
import { CardCVCElement, CardExpiryElement, CardNumberElement, injectStripe } from 'react-stripe-elements';
import { FormGroup, Input, Label } from 'reactstrap';
import { BillingInfoDTO, SubDomainPriceDTO, SubDomainType } from '../../api/model';
import ApiSubdomain from '../../api/api-subdomain';
import Constants from '../../constants';
import UserHelper from '../../lib/user-helper';
import { selectLoggedIn } from '../../store/Account/selectors';
import LoginRegisterDialog from '../Registration/login-register-dialog';
import PricingHeader, { PricingStep } from './pricing-header-indicator';
import { LoginModalMode } from '../Registration/login-enum';
import styled from 'styled-components';
import { VoucherInputModal } from './voucher-input-modal';
import { useState } from 'react';
import { useEffect } from 'react';
import { toast } from 'react-toastify';
import Analytics from '../../lib/user-analytics';

const moment = require('moment');

interface PurchaseFormStripeProps {
    stripe?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
    subDomainName: string;
    subDomainType: SubDomainType;
    planType: SubDomainType;
    returnToPricing: () => void;
    reconfigureSubdomainName: () => void;
    billingInfo: BillingInfoDTO;
    countryCode: string;
    isCreatingStripeToken: boolean;
    stripeError?: Error;
    showCouponInput: boolean;
    validatingCoupon: boolean;
    couponError?: string;
    voucherDetails?: SubDomainPriceDTO;
    baseSubdomainDetails?: SubDomainPriceDTO;
}

const PurchaseFormStripe = (props: PurchaseFormStripeProps) => {
    const loggedIn = useSelector(selectLoggedIn);

    const [isCreatingStripeToken, setIsCreatingStripeToken] = useState<boolean>(false);
    const [showCouponInput, setShowCouponInput] = useState<boolean>(false);
    const [stripeError, setStripeError] = useState<Error | undefined>(undefined);
    const [validatingCoupon, setValidatingCoupon] = useState<boolean>(false);
    const [couponError, setCouponError] = useState<string | undefined>(undefined);
    const [voucherDetails, setVoucherDetails] = useState<SubDomainPriceDTO | undefined>(undefined);
    const [baseSubdomainDetails, setBaseSubdomainDetails] = useState<SubDomainPriceDTO | undefined>(undefined);
    const [validVoucherResponse, setValidVoucherResponse] = useState<boolean>(false);
    const [nameOnCard, setNameOnCard] = useState<string>('');

    useEffect(() => {
        window.scrollTo(0, 0);
        if (props.countryCode) {
            ApiSubdomain.getSubdomainPrice(props.subDomainType, props.countryCode)
                .then((res) => {
                    setBaseSubdomainDetails(res);
                })
                .catch((err) => {
                    setCouponError(err.toString().replace('Error: 400: ', ''));
                });
        }
    }, [props.countryCode, props.subDomainType]);

    useEffect(() => {
        if (stripeError) {
            toast.error(stripeError.message);
        }
    }, [stripeError]);

    const handleSubmitCoupon = (voucher: string) => {
        setValidatingCoupon(true);
        ApiSubdomain.getSubdomainPrice(props.subDomainType, props.countryCode, voucher)
            .then((res) => {
                setVoucherDetails(res);
                setValidVoucherResponse(true);
                Analytics.Event('Plus', 'Clicked to add coupon', voucher);
                // Shows a success message in the voucher input modal before closing
                setTimeout(() => {
                    setShowCouponInput(false);
                    setValidVoucherResponse(false);
                }, 3000);
            })
            .catch((err) => {
                setCouponError(err.toString().replace('Error: 400: ', ''));
            });
        setValidatingCoupon(false);
    };

    const handleCouponError = (error: string) => {
        setCouponError(error);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Precondition: Stripe has initialized
        if (props.stripe === undefined) {
            setStripeError(Error("Stripe.js hasn't loaded"));
            return;
        }

        // Precondition: User has login credentials
        const accessToken = UserHelper.validateAndGetIdToken();
        if (accessToken === undefined) {
            setStripeError(Error("User token wasn't set"));
            return;
        }

        setIsCreatingStripeToken(true);
        setStripeError(undefined);

        if (baseSubdomainDetails?.freeTrialInDays !== undefined && baseSubdomainDetails.freeTrialInDays > 0) {
            ApiSubdomain.createSubdomain(
                props.subDomainName,
                props.subDomainType,
                props.countryCode,
                props.billingInfo,
                baseSubdomainDetails.voucher
            )
                .then(() => {
                    const http = window.location.host.includes('https') ? 'https://' : 'http://';
                    if (window.location.host.split(':')[1]) {
                        const splitDomain = window.location.host.split('.');
                        if (splitDomain[1]) {
                            splitDomain.shift();
                        }
                        const domain = splitDomain.join('.');
                        window.location.href = `${http}${props.subDomainName}.${domain}`;
                    } else {
                        window.location.href = `${http}${props.subDomainName}.${Constants.DOMAIN}`;
                    }
                })
                .catch(() => {
                    toast.error('Error Creating Subdomain');
                });
            return;
        }

        if (voucherDetails?.freeTrialInDays) {
            ApiSubdomain.createSubdomain(
                props.subDomainName,
                props.subDomainType,
                props.countryCode,
                props.billingInfo,
                voucherDetails.voucher
            )
                .then(() => {
                    const http = window.location.host.includes('https') ? 'https://' : 'http://';
                    if (window.location.host.split(':')[1]) {
                        const splitDomain = window.location.host.split('.');
                        if (splitDomain[1]) {
                            splitDomain.shift();
                        }
                        const domain = splitDomain.join('.');
                        window.location.href = `${http}${props.subDomainName}.${domain}`;
                    } else {
                        window.location.href = `${http}${props.subDomainName}.${Constants.DOMAIN}`;
                    }
                })
                .catch(() => {
                    toast.error('Error Creating Subdomain');
                });
            return;
        }

        props.stripe
            .createToken()
            .then((payload) => {
                if (!payload.token || !payload.token.id) {
                    throw new Error(payload.error.message);
                }
                return payload.token.id;
            })
            .then((stripeToken) => {
                if (!stripeToken) {
                    throw new Error('Unable to set token.');
                }
                return ApiSubdomain.createSubdomain(
                    props.subDomainName,
                    props.subDomainType,
                    props.countryCode,
                    props.billingInfo,
                    voucherDetails?.voucher,
                    stripeToken
                );
            })
            .then(() => {
                const http = window.location.host.includes('https') ? 'https://' : 'http://';
                if (window.location.host.split(':')[1]) {
                    const splitDomain = window.location.host.split('.');
                    if (splitDomain[1]) {
                        splitDomain.shift();
                    }
                    const domain = splitDomain.join('.');
                    window.location.href = `${http}${props.subDomainName}.${domain}`;
                } else {
                    window.location.href = `${http}${props.subDomainName}.${Constants.DOMAIN}`;
                }
            })
            .catch((error) => {
                setIsCreatingStripeToken(false);
                setStripeError(Error(error));
                toast.dark('Error with stripe please try again');
            });
    };

    const todayPlusDays = moment(new Date())
        .add(voucherDetails ? voucherDetails.freeTrialInDays : baseSubdomainDetails?.freeTrialInDays, 'days')
        .format('MMMM Do');

    const trialPeriodNotice = (freeTrialDays: number | undefined) => {
        if (freeTrialDays) {
            return `Congratulations, you have a ${freeTrialDays} day free trial period.`;
        } else {
            return;
        }
    };

    const voucherPeriodNotice = (details) => {
        if (details.durationInMonths) {
            return `The voucher is valid for ${details.durationInMonths} month/s.
            Once the coupon expires on ${todayPlusDays}
            you will be required to pay ${details.currency} $${details.totalPrice.toFixed(2)}/month.`;
        } else {
            return `Once the coupon expires on ${todayPlusDays}
            you will be required to pay ${details.currency} $${details.totalPrice.toFixed(2)}/month.`;
        }
    };

    if (!loggedIn) {
        return <LoginRegisterDialog isOpen={true} initialMode={LoginModalMode.LOGIN} />;
    } else
        return (
            <React.Fragment>
                <SubdomainPurchaseContainer>
                    <SubdomainPurchaseForm>
                        <SubdomainPurchaseLogo>
                            <SubdomainLogo src="/assets/subdomain/soar-plus-logo.png" />
                            <SubdomainProPlan />
                        </SubdomainPurchaseLogo>
                        <SubdomainPurchaseInput>
                            <PricingHeader step={PricingStep.Confirm} planType={props.planType} />
                            <SubdomainStepsContainer>
                                {isCreatingStripeToken ? (
                                    <BarLoader width={100} widthUnit="%" color={'#EED926'} />
                                ) : null}
                                <SubdomainPurchaseVoucherContainer>
                                    <SubdomainPurchaseDetails>
                                        <SubdomainPurchaseText>
                                            <p>You're almost done</p>
                                            <h3 title={`${props.subDomainName}.soar.earth`}>
                                                {props.subDomainName}.soar.earth
                                            </h3>
                                        </SubdomainPurchaseText>
                                        <SubdomainPurchaseCouponButton onClick={() => setShowCouponInput(true)}>
                                            I HAVE A COUPON
                                        </SubdomainPurchaseCouponButton>
                                    </SubdomainPurchaseDetails>
                                    <SubdomainPurchaseNotifications>
                                        {baseSubdomainDetails && !voucherDetails ? (
                                            <React.Fragment>
                                                {baseSubdomainDetails.freeTrialInDays === 0 && (
                                                    <p>
                                                        can be purchased for {baseSubdomainDetails.currency}$
                                                        {baseSubdomainDetails.totalPrice.toFixed(2)}
                                                    </p>
                                                )}

                                                {(baseSubdomainDetails.freeTrialInDays || 0) > 0 && (
                                                    <p>{trialPeriodNotice(baseSubdomainDetails.freeTrialInDays)}</p>
                                                )}
                                                <p>{voucherPeriodNotice(baseSubdomainDetails)}</p>
                                            </React.Fragment>
                                        ) : null}

                                        {baseSubdomainDetails && voucherDetails ? (
                                            <React.Fragment>
                                                <p>{trialPeriodNotice(voucherDetails.freeTrialInDays)}</p>
                                                <p>{voucherPeriodNotice(voucherDetails)}</p>
                                            </React.Fragment>
                                        ) : null}
                                    </SubdomainPurchaseNotifications>
                                </SubdomainPurchaseVoucherContainer>
                                <SubdomainPurchaseButtonContainer data-sentry-block>
                                    <PurchaseCreditCardContainer>
                                        <FormGroup>
                                            <Label for="nameOnCard">Card holder name</Label>
                                            <PurchaseCreditCardNameInput
                                                data-sentry-block
                                                placeholder="Name on card"
                                                type="text"
                                                onChange={(e) => setNameOnCard(e.target.value)}
                                                value={nameOnCard}
                                            />
                                        </FormGroup>
                                        <FormGroup>
                                            <Label for="cardNumber">Card Number</Label>
                                            <CustomCardNumberElement
                                                style={{
                                                    base: {
                                                        color: 'rgba(255, 255, 255, 0.6)',
                                                        '::placeholder': {
                                                            color: 'rgba(255, 255, 255, 0.6)',
                                                        },
                                                    },
                                                }}
                                            />
                                        </FormGroup>
                                        <CreditCardIcons src="/assets/payments/mastercard-visa-americanexpress.png" />
                                        <ExpiryCVCContainer>
                                            <FormGroup>
                                                <Label for="expiry">Expiry</Label>
                                                <SoarCardExpiryElement
                                                    style={{
                                                        base: {
                                                            color: 'rgba(255, 255, 255, 0.6)',
                                                            '::placeholder': {
                                                                color: 'rgba(255, 255, 255, 0.6)',
                                                            },
                                                        },
                                                    }}
                                                />
                                            </FormGroup>

                                            <FormGroup>
                                                <Label for="cvc">CVC</Label>
                                                <SoarCardCVCElement
                                                    style={{
                                                        base: {
                                                            color: 'rgba(255, 255, 255, 0.6)',
                                                            '::placeholder': {
                                                                color: 'rgba(255, 255, 255, 0.6)',
                                                            },
                                                        },
                                                    }}
                                                />
                                            </FormGroup>
                                        </ExpiryCVCContainer>
                                        <SubdomainTermsText>
                                            By clicking "CREATE DOMAIN" you agree to being billed{' '}
                                            {voucherDetails ? voucherDetails.currency : baseSubdomainDetails?.currency}{' '}
                                            $
                                            {voucherDetails
                                                ? voucherDetails.totalPrice
                                                : baseSubdomainDetails?.totalPrice}{' '}
                                            each month once your free{' '}
                                            {voucherDetails
                                                ? voucherDetails.freeTrialInDays
                                                : baseSubdomainDetails?.freeTrialInDays}{' '}
                                            day trial has completed on {todayPlusDays}. You may cancel your subscription
                                            anytime. Refer to our{' '}
                                            <a href="https://about.soar.earth/terms.html" target="_blank">
                                                Terms of Service
                                            </a>{' '}
                                            for further details.
                                        </SubdomainTermsText>
                                        <SubdomainButtons>
                                            <SubdomainPurchaseButton
                                                className="subdomain-back"
                                                onClick={props.returnToPricing}
                                            >
                                                BACK TO PRICING
                                            </SubdomainPurchaseButton>
                                            <SubdomainPurchaseButton
                                                className="subdomain-create"
                                                onClick={(e) => handleSubmit(e)}
                                            >
                                                CREATE DOMAIN
                                            </SubdomainPurchaseButton>
                                        </SubdomainButtons>
                                        <CardsImage
                                            className="stripe-image"
                                            src="/assets/payments/powered_by_stripe.png"
                                        />
                                    </PurchaseCreditCardContainer>
                                </SubdomainPurchaseButtonContainer>
                            </SubdomainStepsContainer>
                        </SubdomainPurchaseInput>
                    </SubdomainPurchaseForm>
                </SubdomainPurchaseContainer>
                {showCouponInput && (
                    <VoucherInputModal
                        toggle={() => setShowCouponInput(!showCouponInput)}
                        showCouponInput={showCouponInput}
                        reconfigureSubdomainName={() => props.reconfigureSubdomainName()}
                        handleSubmitCoupon={handleSubmitCoupon}
                        validatingCoupon={validatingCoupon}
                        couponError={couponError}
                        handleCouponError={handleCouponError}
                        validVoucherResponse={validVoucherResponse}
                        voucherDetails={voucherDetails}
                    />
                )}
            </React.Fragment>
        );
};

export default injectStripe(PurchaseFormStripe);

const SubdomainPurchaseContainer = styled.div`
    width: 100vw;
    height: 100%;
    display: flex;

    @media only screen and (max-width: 600px) {
        height: 100vh;
    }
`;

const SubdomainPurchaseForm = styled.div`
    width: 550px;
    margin: auto;
    margin-top: 100px;
    background: rgba(0, 0, 0, 0.8);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 6px;

    @media only screen and (max-width: 600px) {
        flex-direction: column;
        width: 100%;
        margin: 50px 5px 50px 5px;
        overflow-y: auto;
    }
`;

const SubdomainPurchaseLogo = styled.div`
    display: flex;
    flex-direction: row;
`;

const SubdomainLogo = styled.img`
    width: 425px;

    @media only screen and (max-width: 600px) {
        width: 100%;
    }
`;

const SubdomainProPlan = styled.div`
    background-image: url("data:image/svg+xml,%3Csvg width='106' height='64' viewBox='0 0 106 64' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10.4833 0.875975C5.58276 0.875975 3.40912 0.915284 0.128906 1.22974V28.3514H6.13605V18.2889C7.71688 18.4854 9.49531 18.6034 11.2737 18.6034C18.0318 18.6034 20.4425 16.7166 20.4425 9.72001C20.4425 2.80201 18.0318 0.875975 10.4833 0.875975ZM10.3648 14.0045H6.13605V5.47488H10.3648C13.4474 5.47488 14.3959 6.33963 14.3959 9.72001C14.3959 13.1004 13.4474 13.9651 10.3648 14.0045Z' fill='white'/%3E%3Cpath d='M47.4849 19.9005C46.8525 17.1097 44.9951 16.127 43.6118 15.8126V15.616C47.4849 15.1444 48.8286 12.7073 48.8286 8.89457C48.8286 2.68409 45.8645 0.875975 38.7508 0.875975C33.8502 0.875975 31.6371 0.915284 28.3569 1.22974V28.3514H34.285V17.7779H38.5137C40.134 17.7779 40.9244 18.3282 41.4382 20.0577L43.4933 28.3514H49.8561L47.4849 19.9005ZM38.6322 13.1397H34.285V5.51418C35.5891 5.47488 37.0119 5.47488 38.6322 5.47488C41.7939 5.47488 42.7424 6.26101 42.7424 9.28764C42.7424 12.275 41.7939 13.1397 38.6322 13.1397Z' fill='white'/%3E%3Cpath d='M68.3765 0.71875C59.761 0.71875 56.7179 3.9026 56.7179 14.7906C56.7179 25.6786 59.761 28.8624 68.3765 28.8624C76.992 28.8624 80.0746 25.6786 80.0746 14.7906C80.0746 3.9026 76.992 0.71875 68.3765 0.71875ZM68.3765 5.55349C72.4866 5.55349 73.7118 7.59745 73.7118 14.7906C73.7118 21.9837 72.4866 24.0277 68.3765 24.0277C64.2663 24.0277 63.0412 21.9837 63.0412 14.7906C63.0412 7.59745 64.2663 5.55349 68.3765 5.55349Z' fill='white'/%3E%3Cpath d='M10.4833 36.3801C5.58276 36.3801 3.40912 36.4194 0.128906 36.7338V63.8555H6.13605V53.793C7.71688 53.9895 9.49531 54.1074 11.2737 54.1074C18.0318 54.1074 20.4425 52.2207 20.4425 45.2241C20.4425 38.3061 18.0318 36.3801 10.4833 36.3801ZM10.3648 49.5085H6.13605V40.979H10.3648C13.4474 40.979 14.3959 41.8437 14.3959 45.2241C14.3959 48.6045 13.4474 49.4692 10.3648 49.5085Z' fill='white'/%3E%3Cpath d='M34.285 36.7338H28.2778V58.1954C28.2778 61.5364 30.1353 63.9735 34.5221 63.9735C38.3951 63.9735 42.4262 63.9342 46.0621 63.5411L45.7855 58.9815H36.4191C34.8778 58.9815 34.285 58.3919 34.285 56.9768V36.7338Z' fill='white'/%3E%3Cpath d='M70.0996 63.8555H76.2254L68.3607 38.3061C68.1236 37.3234 67.2937 36.7338 66.2661 36.7338H60.5752C59.5871 36.7338 58.7177 37.3234 58.4806 38.3061L50.616 63.8555H56.7812L58.7572 56.741H68.1236L70.0996 63.8555ZM60.0219 52.1028L62.3141 43.8091L62.9069 41.2541H63.9739C64.0925 42.0796 64.3296 42.9836 64.5272 43.8091L66.8194 52.1028H60.0219Z' fill='white'/%3E%3Cpath d='M106 36.7338H100.309V52.3386C100.309 53.8716 100.427 55.3653 100.546 56.8982H100.151C99.5185 55.3653 98.7676 53.7144 98.1748 52.5352L91.2586 38.5026C90.6658 37.3234 90.1125 36.7338 88.8479 36.7338H85.1725C83.7497 36.7338 82.9988 37.4807 82.9988 38.8957V63.8555H88.7293V47.7397C88.7293 46.3247 88.6503 44.831 88.4527 43.416H88.8874C89.2036 44.7524 89.994 46.4426 90.5473 47.6218L97.7005 62.0867C98.2933 63.2659 98.8466 63.8555 100.111 63.8555H103.866C105.288 63.8555 106 63.1087 106 61.6937V36.7338Z' fill='white'/%3E%3C/svg%3E%0A");
    background-repeat: no-repeat;
    width: 106px;
    height: 64px;
    margin-top: 27px;
`;

const SubdomainPurchaseInput = styled.div``;

const SubdomainPurchaseVoucherContainer = styled.div`
    background-color: #eed926;
    border-radius: 6px;
    margin: 5px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: relative;
`;

const SubdomainPurchaseText = styled.div`
    margin: 10px 0px;
    padding-left: 15px;
    width: 100%;

    h3 {
        color: black !important;
        font-size: 24px;
        text-align: left;
        padding-left: 3px;
        font-weight: bold;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    p {
        color: black;
        font-size: 18px;
        padding-left: 3px;
        padding-top: 5px;
        margin-bottom: 2px;
    }

    @media only screen and (max-width: 600px) {
        margin: 10px 0px 10px 0px;

        h3 {
            font-size: 18px;
        }

        p,
        a {
            font-size: 14px;
        }
    }
`;

const SubdomainStepsContainer = styled.div`
    margin: 13px;
`;

const SubdomainPurchaseCouponButton = styled.button`
    border-radius: 6px;
    font-size: 18px;
    padding: 0px 15px;
    cursor: pointer;
    background-color: #eed926;
    margin: 10px 10px 0px 0px;
    height: 30px;
    position: absolute;
    top: 0;
    right: 0;

    :hover {
        opacity: 0.9;
    }

    @media only screen and (max-width: 600px) {
        font-size: 8px;
        padding: 0px 5px;
        margin: 2px 2px 0px 0px;
    }
`;

const SubdomainPurchaseNotifications = styled.div`
    p {
        font-size: 15px;
        margin: 0px 20px;
        padding-bottom: 20px;
    }

    @media only screen and (max-width: 600px) {
        height: 100%;

        p {
            font-size: 12px;
            padding-bottom: 5px;
            margin: 10px;
        }
    }
`;

const SubdomainPurchaseDetails = styled.div`
    border-radius: 6px;
    display: flex;
    flex-direction: row;
    justify-content: inherit;
`;

const SubdomainTermsText = styled.div`
    color: white;

    a {
        color: #eed926 !important;
    }

    @media only screen and (max-width: 600px) {
        margin: 7px;
        font-size: 10px;
    }
`;

const SubdomainPurchaseButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;

    label,
    span {
        color: white;
    }

    @media only screen and (max-width: 600px) {
        flex-direction: column;
        margin: 10px 0px;
    }
`;

const SubdomainPurchaseButton = styled.button`
    border-radius: 6px;
    font-size: 16px;
    padding: 5px 57px;
    cursor: pointer;

    &.subdomain-back {
        background-color: transparent;
        color: white;
        margin-right: 10px;
        border: 1px solid white;
    }

    &.subdomain-create {
        background-color: #eed926;
        color: black;
    }

    :hover {
        opacity: 0.9;
    }

    @media only screen and (max-width: 600px) {
        padding: 5px 0px;
        font-size: 14px;
        width: -webkit-fill-available;
        margin: 0px 5px;

        &.subdomain-back {
            margin-right: 0px;
        }
    }
`;

const PurchaseCreditCardContainer = styled.div`
    margin: 5px;
`;

const PurchaseCreditCardNameInput = styled(Input)`
    background-color: transparent;
    width: 100%;
    height: 40px !important;
    padding: 0px 12px;
    border-radius: 4px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    font-size: 16px;
    outline: none;
    margin-bottom: 10px;
    color: rgba(255, 255, 255, 0.6);
    -webkit-text-fill-color: rgba(255, 255, 255, 0.6) !important;

    :focus,
    :hover {
        background-color: transparent;
        color: rgba(255, 255, 255, 0.6) !important;
        border: 1px solid rgba(238 227 8 / 0.6) !important;
        border-color: rgba(238 227 8 / 0.6) !important;
        box-shadow: 0 0 0 0.2rem rgba(238 227 8 / 0.1) !important;
        -webkit-text-fill-color: rgba(255, 255, 255, 0.6) !important;
    }
`;

const CardsImage = styled.img`
    width: 200px;

    &.stripe-image {
        width: 120px !important;
        background-color: white;
        border-radius: 6px;
        margin-top: 15px;
    }
`;

const ExpiryCVCContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: start;

    .form-group {
        padding-right: 20px !important;
    }
`;

const SubdomainButtons = styled.div`
    display: flex;
    flex-direction: row;
    margin-top: 20px;
    justify-content: space-between;
`;

const CreditCardCSS = `
    text-align: left;
    display: block;
    height: 40px;
    padding: 12px 12px;
    font-size: 16px !important;
    font-weight: 400;
    line-height: 1.2em;
    color: rgba(255, 255, 255, 0.6) !important;
    background-color: transparent;
    background-clip: padding-box;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 0.25rem;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    font-family: 'Exo 2', sans-serif !important;

    :hover {
        border: 1px solid rgba(238 227 8 / 0.6) !important; 
        border-color: rgba(238 227 8 / 0.6) !important;
        box-shadow: 0 0 0 0.2rem rgba(238 227 8 / 0.1) !important;
    }

    :focus {
        color: rgba(255, 255, 255, 0.6) !important;
        border: 1px solid rgba(238 227 8 / 0.6) !important;
        border-color: rgba(238 227 8 / 0.6) !important;
        box-shadow: 0 0 0 0.2rem rgba(238 227 8 / 0.1) !important;
    }
`;

const CustomCardNumberElement = styled(CardNumberElement)`
    ${CreditCardCSS}
    width: 100%;
`;

const SoarCardExpiryElement = styled(CardExpiryElement)`
    ${CreditCardCSS}
    width: 90px;
`;

const SoarCardCVCElement = styled(CardCVCElement)`
    ${CreditCardCSS}
    width: 90px;
`;

const CreditCardIcons = styled.img`
    max-height: 30px;
    float: right;
`;
